import { APP_NAME } from '@trips/shared/config';
import { useStore } from '../data/store';
import { refreshAll } from '../lib/refresh';
import { Link, navigate, type Route, type Tab } from '../lib/router';
import { Button } from '../ui/Button';
import { Icon, type IconName } from '../ui/Icon';
import s from './Sidebar.module.css';

const NAV: {
  tab: Tab;
  label: string;
  icon: IconName;
  active: IconName;
  path: string;
  key: string;
}[] = [
  {
    tab: 'trips',
    label: 'Trips',
    icon: 'suitcase-rolling',
    active: 'suitcase-rolling-fill',
    path: '/',
    key: 'T',
  },
  {
    tab: 'flights',
    label: 'Flights',
    icon: 'airplane-tilt',
    active: 'airplane-tilt-fill',
    path: '/flights',
    key: 'F',
  },
  {
    tab: 'wallet',
    label: 'Wallet',
    icon: 'wallet',
    active: 'wallet-fill',
    path: '/wallet',
    key: 'W',
  },
  {
    tab: 'news',
    label: 'News',
    icon: 'newspaper',
    active: 'newspaper-fill',
    path: '/news',
    key: 'N',
  },
  {
    tab: 'stats',
    label: 'Stats',
    icon: 'chart-bar',
    active: 'chart-bar-fill',
    path: '/stats',
    key: 'S',
  },
];

function SyncLine() {
  const sync = useStore((st) => st.sync);
  let text = 'Up to date';
  if (sync.state === 'syncing') text = 'Syncing';
  else if (sync.state === 'offline')
    text = sync.pending ? `Offline, ${sync.pending} to sync` : 'Offline';
  else if (sync.state === 'error') text = "Couldn't sync";
  else if (sync.pending) text = `${sync.pending} to sync`;
  return (
    <div className={s.syncRow}>
      <span className={`${s.sync} t-caption`} data-state={sync.state}>
        <span className={s.syncDot} aria-hidden="true" />
        {text}
      </span>
      <button
        type="button"
        className={s.refresh}
        aria-label="Refresh"
        title="Refresh"
        data-spinning={sync.state === 'syncing' || undefined}
        onClick={() => void refreshAll()}
      >
        <Icon name="arrows-clockwise" size={16} />
      </button>
    </div>
  );
}

export function Sidebar({ route }: { route: Route }) {
  return (
    <aside className={s.sidebar}>
      <div className={s.brand}>
        <Icon name="airplane-tilt-fill" size={22} />
        <span className="t-headline wide">{APP_NAME}</span>
      </div>
      <button type="button" className={s.search} onClick={() => navigate('/search')}>
        <Icon name="magnifying-glass" size={18} />
        <span>Search</span>
        <kbd className={s.kbd}>/</kbd>
      </button>
      <nav className={s.nav} aria-label="Main">
        {NAV.map((n) => {
          const on = route.tab === n.tab;
          return (
            <Link
              key={n.tab}
              to={n.path}
              transition="fade"
              className={s.item}
              aria-current={on ? 'page' : undefined}
            >
              <Icon name={on ? n.active : n.icon} size={22} />
              <span>{n.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className={s.add}>
        <Button
          variant="primary"
          icon="plus"
          wide
          onClick={() =>
            navigate(route.params.tripId ? `/new?trip=${route.params.tripId}` : '/new', {
              overlay: true,
            })
          }
        >
          Add plan
        </Button>
      </div>
      <div className={s.footer}>
        <Link
          to="/settings"
          transition="fade"
          className={s.item}
          aria-current={route.name.startsWith('settings') ? 'page' : undefined}
        >
          <Icon name="gear-six" size={22} />
          <span>Settings</span>
        </Link>
        <SyncLine />
      </div>
    </aside>
  );
}
